import '../base/list.css';
import { useState, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InputDetail from './detail-input-update';
import filter from '../../../service/filter';
import CONSTANT from '../../../Constant/Constant';
import Storage from '../../../session/storage';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(0)
    },
    nested: {
        paddingLeft: theme.spacing(1),
    },
}));

export default function ListViewInput(props) {
    const classes = useStyles();
    const [open] = useState(true);
    const [label] = useState(props.label);
    const [value] = useState(props.value);
    const labelKey = props.mode === 'Edit' ? Object.keys(value) : Object.keys(value).sort();
    const [newNested, setNewNested] = useState();

    const isLillyManaged = props.entity === 'HCO' && CONSTANT.BRICK_LILLY_MANAGED_SOURCES.includes(props.source);

    const [brickOpen, setBrickOpen] = useState(false);
    const [brickData, setBrickData] = useState(
        isLillyManaged ? {
            [CONSTANT.BRICK_FIELDS.BRICK_TYPE]: [{ value: 'BRK' }],
            [CONSTANT.BRICK_FIELDS.BRICK_ID]: [{ value: '' }],
            [CONSTANT.BRICK_FIELDS.BRICK_EFFECTIVE_START_DATE]: [{ value: moment.utc(new Date()).format('YYYY-MM-DD') }],
            [CONSTANT.BRICK_FIELDS.BRICK_EFFECTIVE_END_DATE]: [{ value: '9999-12-31' }],
            [CONSTANT.BRICK_FIELDS.BRICK_SOURCE]: [{ value: 'LLY-MNGD' }],
        } : null
    );

    /**
     * Handle Delete operation
     */
    const deleteNewItem = () => {
        props.deleteNewItem(props.id)
    }

    /**
     * Handle Nested attribute Changes — must be defined before updateBrickField
     * @param {*} newValue
     * @param {*} newName
     * @param {*} flag
     */
    const setNewNestedValue = async (newValue, newName, flag) => {
        await setNewNested({
            ...newNested,
            [newName]: [newValue]
        });
        let addressValue = { ...newNested, [newName]: [newValue] };
        let allAddressFlag = false;
        let isAddress = CONSTANT.ADD_ADDRESS_ATTRIBUTE.includes(newName);
        if (isAddress) {
            // Exclude BRICK from required field iteration; validate BRICK ID separately
            const fieldstoCheck = CONSTANT.ADD_ADDRESS_ATTRIBUTE.filter(f => f !== 'BRICK');
            fieldstoCheck.forEach((field) => {
                let attributeValue = addressValue[field];
                if (attributeValue === undefined || attributeValue[0].value === '') {
                    allAddressFlag = allAddressFlag || true;
                }
            });
            // For Lilly-managed HCOs, Brick ID is required
            if (isLillyManaged) {
                const brickIdVal = newName === 'BRICK'
                    ? newValue?.[CONSTANT.BRICK_FIELDS.BRICK_ID]?.[0]?.value
                    : brickData?.[CONSTANT.BRICK_FIELDS.BRICK_ID]?.[0]?.value;
                if (!brickIdVal || brickIdVal === '') {
                    allAddressFlag = true;
                }
            }
        }
        // Count only non-BRICK fields for completion check (BRICK is not in Mapping template)
        const nonBrickCount = CONSTANT.ADD_ADDRESS_ATTRIBUTE.filter(f => f !== 'BRICK').length;
        let allAttribute = Object.keys(addressValue).filter(k => k !== 'BRICK').length === nonBrickCount;
        if ((!allAddressFlag) || (allAttribute && allAddressFlag)) {
            props.addNewAttribute({
                ...newNested,
                [newName]: [newValue],
                EffectiveStartDate: [{ value: moment.utc(new Date()).format("YYYY-MM-DD") }],
                EffectiveEndDate: [{ value: "9999-12-31" }],
                IsNew: true,
                // Include BRICK for Lilly-managed when the current field is not BRICK itself
                // (when newName === 'BRICK', [newName]: [newValue] already sets it above)
                ...(isLillyManaged && newName !== 'BRICK' && brickData ? { BRICK: [brickData] } : {})
            }, (isAddress ? allAddressFlag : flag));
        }
    }

    /**
     * Update a single BRICK field and propagate to parent
     */
    const updateBrickField = (brickKey, newVal) => {
        const updated = { ...(brickData || {}), [brickKey]: [{ value: newVal }] };
        setBrickData(updated);
        setNewNestedValue(updated, 'BRICK', false);
    };

    useEffect(() => {
        if (props.change) {
            setNewNested()
        }
    }, [props.change])

    // Show BRICK accordion once the user has selected a Country and the HCO is Lilly-managed
    const selectedCountry = newNested?.Country?.[0]?.value;
    // A country is Lilly-managed when it appears in getAllCountryList() as a bare name (no '#' separator).
    // Vendor-managed countries are stored as "CountryName#VendorSource".
    const isCountryLillyManaged = selectedCountry
        ? (Storage.getAllCountryList() || []).some(entry => entry === selectedCountry)
        : false;
    const showBrickAccordion = isLillyManaged && isCountryLillyManaged;

    return (
        <List
            component="nav"
            aria-labelledby="nested-list-subheader"
            className={classes.root}
        >
            <ListItem className={open ? ' bg-muted' : ''}>
                <ListItemText className='_wg-dynamic-text' primary={label} />
                <DeleteIcon className='pointer text-primary' onClick={deleteNewItem} />
            </ListItem>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" className="bg-grey" >
                    <ListItem className={classes.nested}>
                        <ListItemText primary={
                            labelKey?.map((labels, i) => {
                                let keysValue = labels;
                                let propertyValue = value[labels];
                                if (labels.length > 3) {
                                    labels = labels.replace(/([A-Z])/g, ' $1')
                                        .replace(/^./, (str) => str.toUpperCase())
                                }
                                labels = filter.getLabelName(labels !== undefined ? labels.trim() : labels);
                                return (
                                    <div className='mt-2 _wg-dynamic-text' key={i}>
                                        <span className="text-muted mb-2  mt-2">{labels + (CONSTANT.REQUIRED_HCP.includes(labels) ? ' *' : '') + ':' || ''}</span>
                                        <InputDetail
                                            vendor={props.vendor}
                                            error={props.error}
                                            date={propertyValue === 'date'}
                                            type={propertyValue}
                                            label={labels} value={''}
                                            updatedValue={(newValue, flag) => { setNewNestedValue(newValue, keysValue, flag) }}
                                            newValue={props.newValue}
                                            source={props.source}
                                            entity={props.entity} />
                                    </div>)
                            })
                        } />
                    </ListItem>
                </List>

                {/* BRICK accordion — shown after Country is selected for Lilly-managed HCOs */}
                {showBrickAccordion && (
                    <List component="nav">
                        <ListItemButton
                            onClick={() => setBrickOpen(prev => !prev)}
                        >
                            <ListItemText className='_wg-dynamic-text' primary="BRICK" />
                            {brickOpen ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={brickOpen} timeout="auto" unmountOnExit>
                            <div className="row container w-100 p-3 pt-0" style={{ marginLeft: 0 }}>
                                {[
                                    { id: 'BrickType',               key: CONSTANT.BRICK_FIELDS.BRICK_TYPE,                   name: 'Brick Type',                 readOnly: true,  date: false },
                                    { id: 'BrickId',                 key: CONSTANT.BRICK_FIELDS.BRICK_ID,                     name: 'Brick Id',                   readOnly: false, date: false },
                                    { id: 'BrickEffectiveStartDate', key: CONSTANT.BRICK_FIELDS.BRICK_EFFECTIVE_START_DATE,    name: 'Brick Effective Start Date', readOnly: true,  date: true  },
                                    { id: 'BrickEffectiveEndDate',   key: CONSTANT.BRICK_FIELDS.BRICK_EFFECTIVE_END_DATE,      name: 'Brick Effective End Date',   readOnly: false, date: true  },
                                    { id: 'BrickSource',             key: CONSTANT.BRICK_FIELDS.BRICK_SOURCE,                 name: 'Source',                     readOnly: true,  date: false },
                                ].map((field, idx) => {
                                    const brickVal = brickData?.[field.key]?.[0]?.value || '';
                                    return (
                                        <div key={idx} className="col-sm-12 col-md-6 mt-2 _wg-dynamic-text">
                                            <span className="text-muted mb-2 mt-2">
                                                {field.name}
                                                {field.id === 'BrickId' && <span style={{ color: 'red' }}> *</span>}
                                                {':'}
                                            </span>
                                            {field.readOnly ? (
                                                <div style={{ borderBottom: '1px solid rgba(0,0,0,0.42)', padding: '6px 0 7px', minHeight: '32px' }}>
                                                    <span className="_wg-exclude">{brickVal}</span>
                                                </div>
                                            ) : (
                                                <InputDetail
                                                    date={field.date}
                                                    type="text"
                                                    value={brickVal}
                                                    label={field.name}
                                                    entity={props.entity}
                                                    error={props.error}
                                                    updatedValue={(newValue) => updateBrickField(field.key, newValue.value)}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </Collapse>
                    </List>
                )}
            </Collapse>
        </List>
    );
}
